import React from 'react'
import PropTypes from 'prop-types'
import ofllineImg from '../assets/images/offline.gif'

/**
 * NoInternetConnection Component
 *
 * @module common/NoInternetConnection
 * @description This React component monitors the user's internet connectivity status and dynamically renders
 * either the children passed to it (if online) or a custom "No Internet Connection" message (if offline).
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components to render when the user is online.
 * @example
 * // Usage Example
 * <NoInternetConnection>
 *   <YourComponent />
 * </NoInternetConnection>
 *
 * @returns {React.Element} Renders an page with the reload the page button.
 */
export const NoInternetConnection = ({ children }) => {
  // state variable holds the state of the internet connection
  const [isOnline, setOnline] = React.useState(true)

  // On initization set the isOnline state.
  React.useEffect(() => {
    setOnline(navigator.onLine)
  }, [])

  // event listeners to update the state
  window.addEventListener('online', () => {
    setOnline(true)
  })

  window.addEventListener('offline', () => {
    setOnline(false)
  })

  // if user is online, return the child component else return a custom component
  if (isOnline) {
    return children
  } else {
    return (
      <div className="trtui-auth-page-wrapper trtui-auth-bg-cover trtui-py-5 trtui-d-flex trtui-justify-content-center trtui-align-items-center trtui-min-vh-100">
        <div className="trtui-bg-overlay" />
        <div className="trtui-auth-page-content trtui-overflow-hidden trtui-pt-lg-5">
          <div className="trtui-container">
            <div className="trtui-row trtui-justify-content-center">
              <div className="trtui-col-xl-5">
                <div className="trtui-card trtui-overflow-hidden">
                  <div className="trtui-card-body trtui-p-4">
                    <div className="trtui-text-center">
                      <img src={ofllineImg} alt="No Internet" height={210} />
                      <h3 className="trtui-mt-4 trtui-fw-semibold">Whoooops!</h3>
                      <p className="trtui-text-muted trtui-fs-18">No internet connection!</p>
                      <p className="trtui-text-muted trtui-mb-4 trtui-fs-14">
                        Check your connection or try again.
                      </p>
                      <button
                        className="trtui-btn trtui-btn-secondary trtui-btn-border"
                        onClick={() => window.location.reload()}
                      >
                        <i className="ri-refresh-line trtui-align-bottom" /> Refresh
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

NoInternetConnection.propTypes = {
  children: PropTypes.array,
}
